import { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { gsap } from "@/lib/gsap";
import { CONTACT } from "@/constants/testIds";
import { useT } from "@/i18n";
import useReveal from "@/hooks/useReveal";

const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;
const TO_EMAIL = process.env.REACT_APP_CONTACT_EMAIL;

export default function Contact() {
  const ref = useRef(null);
  const formRef = useRef(null);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ 
    name: "", 
    email: "", 
    businessName: "",
    businessLinks: "",
    subject: "", 
    message: "",
    budget: "",
    includeDemo: false
  });
  const { t } = useT();

  useReveal(ref, () => {
    const ctx = gsap.context(() => {
      gsap.from("[data-anim='c-title']", { opacity: 0, y: 60, duration: 1.05, ease: "power3.out" });
      gsap.from("[data-anim='c-field']", { opacity: 0, y: 30, duration: 0.7, stagger: 0.08, delay: 0.25 });
      gsap.from("[data-anim='c-info']", { opacity: 0, y: 30, duration: 0.7, stagger: 0.1, delay: 0.4 });
    }, ref);
    return () => ctx.revert();
  });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.businessName || !form.budget || !form.message) {
      toast.error(t("contact.toastEmpty"));
      return;
    }
    if (!SERVICE_ID || !TEMPLATE_ID || !PUBLIC_KEY) {
      toast.error(t("contact.toastNoConfig"));
      return;
    }
    setSending(true);
    try {
      await emailjs.send(SERVICE_ID, TEMPLATE_ID, {
        from_name: form.name,
        from_email: form.email,
        business_name: form.businessName,
        business_links: form.businessLinks || "Not provided",
        subject: form.subject || t("contact.subjectDefault"),
        message: form.message,
        budget: form.budget,
        include_demo: form.includeDemo ? "Yes" : "No",
        to_email: TO_EMAIL,
        reply_to: form.email,
      }, { publicKey: PUBLIC_KEY });
      toast.success(t("contact.toastSuccess"));
      setForm({ name: "", email: "", businessName: "", businessLinks: "", subject: "", message: "", budget: "", includeDemo: false });
    } catch (err) {
      console.error(err);
      toast.error(t("contact.toastFail"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      ref={ref}
      className="relative w-full h-screen overflow-hidden grain flex items-center justify-center px-5 sm:px-6 md:px-12 lg:px-20 py-12 sm:py-16 md:py-20"
      style={{ background: "var(--ink)" }}
      data-testid={CONTACT.root}
    >
      <div className="orb" style={{ width: 600, height: 600, background: "radial-gradient(circle, var(--violet) 0%, transparent 70%)", top: "-22%", left: "-10%", opacity: 0.16 }} />
      <div className="orb" style={{ width: 440, height: 440, background: "radial-gradient(circle, var(--green) 0%, transparent 70%)", bottom: "-18%", right: "-12%", opacity: 0.08 }} />


      <div 
        className="relative z-10 w-full max-w-[1280px] max-h-[75vh] overflow-y-auto px-5 sm:px-6 md:px-8 lg:px-10 py-8 sm:py-10 md:py-14 lg:py-16"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(139, 92, 246, 0.3) transparent'
        }}
      >
        <style>{`
          .relative.z-10::-webkit-scrollbar {
            width: 8px;
          }
          .relative.z-10::-webkit-scrollbar-track {
            background: transparent;
          }
          .relative.z-10::-webkit-scrollbar-thumb {
            background: rgba(139, 92, 246, 0.3);
            border-radius: 4px;
          }
          .relative.z-10::-webkit-scrollbar-thumb:hover {
            background: rgba(139, 92, 246, 0.5);
          }
        `}</style>

        <div className="mb-12 sm:mb-16 md:mb-20">
          <div data-anim="c-title" className="flex flex-col gap-2 sm:gap-3 mb-5 sm:mb-7">
            <span className="eyebrow text-white/40">Get in Touch</span>
            <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-semibold text-white leading-[1.1]">
              Let's <span style={{ color: "var(--violet-soft)" }}>talk</span>.
            </h2>
          </div>
          <p className="text-white/60 text-[0.9375rem] sm:text-base md:text-lg max-w-2xl leading-relaxed">
            Tell us about your project and we'll send a detailed quotation within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 sm:gap-14 lg:gap-16 xl:gap-24">
          <form ref={formRef} onSubmit={onSubmit} data-anim="c-form" data-testid={CONTACT.form} className="lg:col-span-3 space-y-6 sm:space-y-7">
            <div data-anim="c-field" className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7">
              <div>
                <label className="eyebrow text-white/40 block mb-2 sm:mb-3">{t("contact.name")}</label>
                <input data-testid={CONTACT.name} className="input-line" name="name" value={form.name} onChange={onChange} placeholder={t("contact.namePh")} />
              </div>
              <div>
                <label className="eyebrow text-white/40 block mb-2 sm:mb-3">{t("contact.email")}</label>
                <input data-testid={CONTACT.email} className="input-line" name="email" type="email" value={form.email} onChange={onChange} placeholder={t("contact.emailPh")} />
              </div>
            </div>

            <div data-anim="c-field" className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7">
              <div>
                <label className="eyebrow text-white/40 block mb-2 sm:mb-3">{t("contact.businessName")} *</label>
                <input className="input-line" name="businessName" value={form.businessName} onChange={onChange} placeholder={t("contact.businessNamePh")} />
              </div>
              <div>
                <label className="eyebrow text-white/40 block mb-2 sm:mb-3">{t("contact.businessLinks")}</label>
                <input className="input-line" name="businessLinks" value={form.businessLinks} onChange={onChange} placeholder={t("contact.businessLinksPh")} />
              </div>
            </div>

            <div data-anim="c-field">
              <label className="eyebrow text-white/40 block mb-2 sm:mb-3">{t("contact.budget")} *</label>
              <select className="input-line" name="budget" value={form.budget} onChange={onChange}>
                <option value="">Select your budget range</option>
                <option value="under-300">{t("contact.budget.option1")}</option>
                <option value="300-800">{t("contact.budget.option2")}</option>
                <option value="800-1500">{t("contact.budget.option3")}</option>
                <option value="1500-plus">{t("contact.budget.option4")}</option>
                <option value="not-sure">{t("contact.budget.option5")}</option>
              </select>
            </div>

            <div data-anim="c-field">
              <label className="eyebrow text-white/40 block mb-2 sm:mb-3">{t("contact.subject")}</label>
              <input data-testid={CONTACT.subject} className="input-line" name="subject" value={form.subject} onChange={onChange} placeholder={t("contact.subjectPh")} />
            </div>

            <div data-anim="c-field">
              <label className="eyebrow text-white/40 block mb-2 sm:mb-3">{t("contact.message")}</label>
              <textarea data-testid={CONTACT.message} className="input-line min-h-[120px] resize-none" name="message" value={form.message} onChange={onChange} placeholder={t("contact.messagePh")} />
            </div>

            <div data-anim="c-field" className="pt-1">
              <label className="flex items-start gap-3 cursor-pointer text-white/80 hover:text-white transition-colors">
                <input
                  type="checkbox"
                  name="includeDemo"
                  checked={form.includeDemo}
                  onChange={onChange}
                  className="mt-1 w-5 h-5 rounded border-white/20 bg-white/10 checked:bg-violet focus:ring-violet focus:ring-2"
                />
                <div>
                  <span className="font-semibold block mb-1 text-sm sm:text-base">{t("contact.demo")}</span>
                  <span className="text-xs sm:text-sm text-white/60">{t("contact.demoInfo")}</span>
                </div>
              </label>
            </div>

            <div data-anim="c-field" className="pt-3 sm:pt-5">
              <button type="submit" disabled={sending} data-testid={CONTACT.submit} className="btn-pill btn-pill-violet disabled:opacity-60">
                {sending ? (<><Loader2 size={14} className="animate-spin" /> {t("contact.sending")}</>) : (<>{t("contact.send")} <Send size={14} /></>)}
              </button>
            </div>
          </form>

          <div data-anim="c-info-block" className="lg:col-span-2 space-y-8 sm:space-y-10 text-white">
            <div data-anim="c-info">
              <div className="eyebrow text-white/40 mb-2 sm:mb-3">{t("contact.emailLabel")}</div>
              <a href={`mailto:${TO_EMAIL}`} className="link-line">hello@makario.id</a>
            </div>
            <div data-anim="c-info">
              <div className="eyebrow text-white/40 mb-2 sm:mb-3">{t("contact.phoneLabel")}</div>
              <a href="tel:+622150123456" className="link-line">+62 21 5012 3456</a>
            </div>
            <div data-anim="c-info">
              <div className="eyebrow text-white/40 mb-2 sm:mb-3">{t("contact.officeLabel")}</div>
              <p className="text-white/75 body-text text-sm whitespace-pre-line leading-relaxed">{t("contact.officeBody")}</p>
            </div>
            <div data-anim="c-info" className="pt-6 sm:pt-8 border-t border-white/10">
              <div className="eyebrow text-white/40 mb-2 sm:mb-3">{t("contact.branchLabel")}</div>
              <p className="text-white/75 text-sm leading-relaxed">{t("contact.branchBody")}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
